import {ArrowBothIcon, ColumnsIcon, ListUnorderedIcon, ProjectIcon, RowsIcon} from '@primer/octicons-react'
import {AriaAlert, Label, Spinner} from '@primer/react'
import {useMemo} from 'react'

import type {ViewConfiguration} from '../../contexts/views/view-configuration'
import type {Layout} from '../../utils/saved-view-url-params'
import {getFieldsForCapability} from './get-fields-for-capability'
import {Capability, type FieldDefinition, type FieldsData} from './fields'
import type {FieldOption} from '../view-options/field-option'
import {ViewOptionsMenu} from '../view-options/ViewOptionsMenu'
import {getFieldIcon} from './field-icons'
import styles from './SavedViewOptionsMenu.module.css'

const LAYOUT_OPTIONS = [
  {value: 'list', label: 'List', icon: ListUnorderedIcon},
  {value: 'board', label: 'Board', icon: ProjectIcon},
] satisfies ReadonlyArray<{value: Layout; label: string; icon: typeof ListUnorderedIcon}>

function toFieldOption({identifier, label, icon}: FieldDefinition): FieldOption {
  return {identifier, label, icon: getFieldIcon(icon)}
}

/** Sort's leading visual, matching memex: a two-way arrow rotated to point up/down. */
function SortByIcon({size}: {size?: number}) {
  return <ArrowBothIcon size={size} className={styles.sortIcon} />
}

const noopSelect = () => {}

interface SavedViewOptionsMenuProps {
  fields: FieldsData | undefined
  /** True while the field definitions are being fetched asynchronously. */
  isPending?: boolean
  /** True when the async fetch for the field definitions failed. */
  isError?: boolean
  configuration: ViewConfiguration
  onLayoutChange: (layout: Layout) => void
  onVisibleFieldChange: (identifier: string, visible: boolean) => void
  onGroupByChange?: (identifier: string) => void
  onSwimlaneByChange?: (identifier: string | null) => void
  onSortByChange?: (identifier: string | null) => void
}

/** Controlled saved-view options menu shared by domain-specific hosts. */
export function SavedViewOptionsMenu({
  fields: view,
  isPending = false,
  isError = false,
  configuration,
  onLayoutChange,
  onVisibleFieldChange,
  onGroupByChange,
  onSwimlaneByChange,
  onSortByChange,
}: SavedViewOptionsMenuProps) {
  const {layout, groupBy, swimlaneBy, sorts, visibleFields} = configuration
  const visibleFieldIdentifiers = useMemo(() => new Set(visibleFields), [visibleFields])
  const groupFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.GROUPABLE).map(toFieldOption) : []),
    [view],
  )
  const columnFields = useMemo(
    () => groupFields.map(field => ({...field, disabled: field.identifier === swimlaneBy})),
    [groupFields, swimlaneBy],
  )
  const swimlaneFields = useMemo(
    () => groupFields.map(field => ({...field, disabled: field.identifier === groupBy})),
    [groupFields, groupBy],
  )
  const attributeFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(toFieldOption) : []),
    [view],
  )
  const sortFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.SORTABLE).map(toFieldOption) : []),
    [view],
  )

  return (
    <ViewOptionsMenu triggerLabel="View options" title="View options">
      <ViewOptionsMenu.Layout
        label="Layout"
        trailing={layout === 'board' ? <Label variant="success">Alpha</Label> : undefined}
        options={LAYOUT_OPTIONS}
        value={layout}
        onSelect={onLayoutChange}
      />

      {layout === 'board' && (
        <>
          <ViewOptionsMenu.Group
            label="Configuration"
            status={
              isPending ? (
                <div className={styles.status}>
                  <Spinner srText="Loading view options" />
                </div>
              ) : isError ? (
                <AriaAlert className={styles.status}>Unable to load view options</AriaAlert>
              ) : null
            }
          >
            {view && (
              <>
                <ViewOptionsMenu.SingleSelect
                  icon={ColumnsIcon}
                  label="Column by"
                  placeholder="None"
                  fields={columnFields}
                  value={groupBy}
                  onSelect={onGroupByChange ?? noopSelect}
                />
                <ViewOptionsMenu.SingleSelect
                  icon={RowsIcon}
                  label="Swimlane by"
                  placeholder="None"
                  fields={swimlaneFields}
                  value={swimlaneBy}
                  onSelect={onSwimlaneByChange ?? noopSelect}
                  onClear={onSwimlaneByChange ? () => onSwimlaneByChange(null) : undefined}
                />
                <ViewOptionsMenu.SingleSelect
                  icon={SortByIcon}
                  label="Sort by"
                  placeholder="None"
                  fields={sortFields}
                  value={sorts[0]?.field ?? null}
                  onSelect={onSortByChange ?? noopSelect}
                  onClear={onSortByChange ? () => onSortByChange(null) : undefined}
                />
              </>
            )}
          </ViewOptionsMenu.Group>
          {view && (
            <ViewOptionsMenu.ToggleGroup
              label="Visible fields"
              fields={attributeFields}
              selectedIds={visibleFieldIdentifiers}
              onToggle={onVisibleFieldChange}
            />
          )}
        </>
      )}
    </ViewOptionsMenu>
  )
}
